/**
 * LoadingSpinner.tsx — Animated loading indicator
 *
 * Purpose:
 *   Provides a CSS-animated spinner for async loading states in the admin
 *   console. Can be used inline or as a full-page overlay.
 *
 * Public API:
 *   - LoadingSpinner  Animated spinner component
 *
 * @module packages/billing-admin/src/components/LoadingSpinner
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';

interface LoadingSpinnerProps {
  /** Spinner size (default: 'md'). */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to render as a full-viewport overlay. */
  fullPage?: boolean;
  /** Accessible label for screen readers. */
  label?: string;
}

const SIZE_CLASSES = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-12 w-12 border-4',
} as const;

/**
 * LoadingSpinner — animated circular progress indicator.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function LoadingSpinner({
  size = 'md',
  fullPage = false,
  label = 'Loading',
}: LoadingSpinnerProps): React.JSX.Element {
  const spinner = (
    <div
      role="status"
      aria-label={label}
      className={`${SIZE_CLASSES[size]} animate-spin rounded-full border-gray-300 border-t-blue-600`}
    />
  );

  if (fullPage) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-40">
        {spinner}
      </div>
    );
  }

  return spinner;
}
